import { useEffect, useRef, useState } from "react";
import { Box, Button, ButtonBase, MenuItem, Select, TextField, Tooltip } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ResetIcon from "@mui/icons-material/RotateLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { alpha } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import StyledSwitch from "@plannel/components/ui/StyledSwitch";
import { FS } from "@plannel/utils/font-util";

/*
 * Workbench Measure 오른쪽 편집 패널.
 *
 * 왼쪽 목록에서 고른 measure 하나의 속성을 전부 펼쳐 편집한다.
 * 값 저장은 여기서 하지 않는다 — onChange(key, value) 로 올려주면 페이지가 AG Grid 행에
 * 그대로 반영하고(setDataValue), 변경행 추적·저장·검증은 기존 경로를 그대로 탄다.
 *
 * props
 *   title    : measure 이름(헤더에 크게)
 *   badges   : [{ label, kind: "chip" | "fx" }]  이름 옆 표식
 *   toggles  : [{ key, label, checked, disabled }]
 *   fields   : [{ key, label, kind: "text"|"select"|"formula"|"action", value, options?, disabled?, wide? }]
 *              select 의 options 는 [{ value, label }] — value 는 그리드에 그대로 넣을 원본(객체 가능)
 *              action 은 값을 여기서 고르지 않고 **다른 모달을 여는** 칸이다(계층 필터 등):
 *              지금 값을 보여 주고 onClick 으로 그 모달을 연다. onChange 는 부르지 않는다.
 *   onChange : (key, value) => void
 *   empty    : 선택된 행이 없을 때 보여줄 문구
 */

// 화면 전역 SCSS(.content-main .MuiTypography-root)가 글자 크기를 덮어써서 Typography 를 쓰지 않는다.
const LABEL_SX = {
  fontSize: FS.CAPTION,
  fontWeight: 600,
  letterSpacing: ".08em",
  textTransform: "uppercase",
  color: "text.disabled",
};

/*
 * 글자 입력은 타이핑 중에 값을 올려보내지 않는다.
 * 한 글자마다 그리드에 쓰면 AG Grid 가 바뀐 셀로 포커스를 가져가 두 번째 글자부터 입력칸을 벗어난다.
 * 그래서 입력 중에는 여기 draft 로만 들고 있다가 포커스가 빠지거나 Enter 일 때 한 번 올린다.
 */
const PanelTextField = ({ field, onCommit }) => {
  const { insert, disabled } = field;
  const incoming = field.value ?? "";
  const [draft, setDraft] = useState(incoming);
  const committedRef = useRef(incoming);
  const inputRef = useRef(null);
  // 이미 내려와 있던 요청은 처음 그릴 때 적용된 것으로 본다 — measure 를 바꿔 끼울 때마다 다시 끼워 넣지 않도록.
  const insertSeqRef = useRef(field.insert?.seq ?? 0);

  useEffect(() => {
    // 다른 measure 를 고르거나 바깥에서 값이 바뀐 경우에만 입력칸을 새 값으로 맞춘다.
    if (incoming !== committedRef.current) {
      committedRef.current = incoming;
      setDraft(incoming);
    }
  }, [incoming]);

  /*
   * 바깥(measure 선택 메뉴)에서 내려온 조각을 커서 자리에 끼워 넣는다.
   * 같은 조각을 다시 고를 수도 있으므로 seq 로 새 요청인지 구분한다.
   */
  useEffect(() => {
    const request = insert;
    if (!request || request.seq === insertSeqRef.current) return;
    insertSeqRef.current = request.seq;
    if (disabled) return;

    const input = inputRef.current;
    const caret = input ? input.selectionStart : null;

    setDraft(prev => {
      const at = caret ?? prev.length;
      const before = prev.slice(0, at);
      /*
       * 메뉴가 열릴 때 포커스를 가져가서 사용자가 친 "[" 가 입력칸에 안 들어오는 경우가 있다.
       * 커서 앞이 "[" 가 아니면 여는 괄호까지 같이 넣는다(이미 있으면 이름만).
       */
      const text = before.endsWith("[") ? request.text : `[${request.text}`;
      const next = `${before}${text}${prev.slice(at)}`;
      const nextCaret = at + text.length;
      // 상태 반영 후에 커서를 끼워 넣은 뒤로 돌려놓는다.
      window.requestAnimationFrame(() => {
        input?.focus();
        input?.setSelectionRange(nextCaret, nextCaret);
      });
      return next;
    });
  }, [insert, disabled]);

  const commit = () => {
    if (draft === committedRef.current) return;
    committedRef.current = draft;
    onCommit(field.key, draft);
  };

  const isFormula = field.kind === "formula";

  return (
    <TextField
      size="small"
      disabled={field.disabled}
      value={draft}
      inputRef={inputRef}
      placeholder={field.placeholder}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit();
        }
        field.onKeyDown?.(e);
      }}
      sx={{
        // 수식도 다른 칸과 같은 글꼴·크기로 둔다 — Windows 에서 monospace 가 Courier 계열로 떨어져 혼자 튀었다.
        "& .MuiInputBase-input": {
          fontSize: FS.SUB,
          color: isFormula ? "primary.dark" : undefined,
        },
      }}
    />
  );
};

const MeasureDetailPanel = ({ selected, instanceKey, title, placeholderTitle, badges = [], toggles = [], fields = [], onChange, onDelete, deleteLabel, empty }) => {
  // 이름이 아직 없는(방금 추가한) measure 도 편집할 수 있어야 하므로 title 이 아니라 selected 로 판단한다.
  if (!selected) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", p: 3 }}>
        <Box component="span" sx={{ fontSize: FS.SUB, color: "text.disabled" }}>{empty}</Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 2.5, overflow: "auto", height: "100%", boxSizing: "border-box" }}>
      {/* 헤더 — 이름 + 표식 (+ 지울 수 있는 measure 면 삭제) */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Box component="span" sx={{ fontSize: FS.H1, fontWeight: 600, color: title ? "text.primary" : "text.disabled" }}>
          {title || placeholderTitle}
        </Box>
        {badges.map(badge => (
          <Box
            key={badge.label}
            component="span"
            sx={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              height: 16, px: badge.kind === "fx" ? 0 : 0.75, minWidth: badge.kind === "fx" ? 16 : undefined,
              borderRadius: 0.75, fontSize: FS.CAPTION, fontWeight: 600, letterSpacing: ".04em",
              fontStyle: badge.kind === "fx" ? "italic" : "normal",
              fontFamily: badge.kind === "fx" ? "ui-monospace, SFMono-Regular, Menlo, monospace" : undefined,
              bgcolor: badge.kind === "fx" ? "primary.main" : theme => alpha(theme.palette.primary.main, 0.1),
              color: badge.kind === "fx" ? "primary.contrastText" : "primary.dark",
            }}
          >
            {badge.label}
          </Box>
        ))}
        {onDelete && (
          <Button
            size="small"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={onDelete}
            sx={{ ml: "auto", fontSize: FS.SUB }}
          >
            {deleteLabel}
          </Button>
        )}
      </Box>

      {/* 켜고 끄기 */}
      {toggles.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
          {toggles.map(toggle => (
            <Box key={toggle.key} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <StyledSwitch
                checked={!!toggle.checked}
                disabled={toggle.disabled}
                onChange={e => onChange(toggle.key, e.target.checked)}
              />
              <Box component="span" sx={{ fontSize: FS.SUB, color: toggle.disabled ? "text.disabled" : "text.primary" }}>
                {toggle.label}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* 속성 */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" },
          gap: 2,
        }}
      >
        {fields.map(field => (
          <Box key={field.key} sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0, gridColumn: field.wide ? "1 / -1" : undefined }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box component="span" sx={LABEL_SX}>{field.label}</Box>
              {/* 작성법 안내 — 도움말이 필요한 칸(수식) 바로 옆에 둔다. */}
              {field.onHelp && (
                <ButtonBase
                  onClick={field.onHelp}
                  sx={{
                    width: 14, height: 14, borderRadius: "50%", fontSize: FS.CAPTION, lineHeight: 1,
                    border: 1, borderColor: "divider", color: "text.disabled",
                    "&:hover": { color: "primary.main", borderColor: "primary.main" },
                  }}
                >
                  ?
                </ButtonBase>
              )}
              {/* 되돌리기 — 값을 바꾼 칸에서만 나타난다(이름을 기본값으로) */}
              {field.onReset && (
                <Tooltip title={field.resetLabel ?? ""}>
                  <ButtonBase
                    onClick={field.onReset}
                    sx={{
                      display: "inline-flex", alignItems: "center", gap: 0.25,
                      height: 16, px: 0.5, borderRadius: 0.75,
                      fontSize: FS.CAPTION, letterSpacing: 0, textTransform: "none",
                      color: "primary.main",
                      "&:hover": { bgcolor: theme => alpha(theme.palette.primary.main, 0.08) },
                    }}
                  >
                    <ResetIcon sx={{ fontSize: 13 }} />
                    {field.resetText}
                  </ButtonBase>
                </Tooltip>
              )}
            </Box>
            {field.kind === "action" ? (
              /*
               * 값을 고르는 일은 뒤에 열리는 모달이 한다 — 여기서는 지금 값을 보여 주고 그 모달을 연다.
               * 입력칸과 같은 높이·테두리로 두어 폼 안에서 한 줄만 튀지 않게 한다.
               */
              <Button
                fullWidth
                size="small"
                variant="outlined"
                color="inherit"
                disabled={field.disabled}
                onClick={field.onClick}
                endIcon={<ChevronRightIcon />}
                sx={{
                  justifyContent: "space-between", textTransform: "none", fontWeight: 400,
                  fontSize: FS.SUB, height: 33, px: 1.5,
                  borderColor: "rgba(0, 0, 0, 0.23)", color: "text.primary",
                  "& .MuiButton-endIcon": { color: "text.disabled", ml: 0.5 },
                }}
              >
                <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {field.value}
                </Box>
              </Button>
            ) : field.kind === "select" ? (
              <Select
                size="small"
                displayEmpty
                disabled={field.disabled}
                value={field.options?.find(opt => opt.selected)?.key ?? ""}
                onChange={e => {
                  const picked = field.options?.find(opt => opt.key === e.target.value);
                  onChange(field.key, picked ? picked.value : null);
                }}
                sx={{ "& .MuiSelect-select": { fontSize: FS.SUB, py: 0.85 } }}
              >
                {(field.options ?? []).map(opt => (
                  <MenuItem key={opt.key} value={opt.key} disabled={opt.disabled} sx={{ fontSize: FS.SUB }}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <PanelTextField key={`${instanceKey}-${field.key}`} field={field} onCommit={onChange} />
            )}
            {field.help && (
              <Box
                sx={{
                  display: "flex", alignItems: "flex-start", gap: 0.5, mt: 0.25,
                  px: 1, py: 0.5, borderRadius: 0.75,
                  bgcolor: theme => alpha(theme.palette.primary.main, 0.06),
                  color: "text.secondary", fontSize: FS.BODY, lineHeight: 1.5,
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 14, mt: "1px", flexShrink: 0, color: "primary.main" }} />
                <Box component="span">{field.help}</Box>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default MeasureDetailPanel;
